import React, { useState } from 'react';
import { PlusIcon, SearchIcon, FilterIcon } from 'lucide-react';
export function ReconciliationList({
  reconciliations,
  onStartNew,
  onViewReconciliation,
  locale = 'en-US'
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const filteredReconciliations = reconciliations.filter(reconciliation => {
    // Search term filter
    const matchesSearch = reconciliation.vendor.toLowerCase().includes(searchTerm.toLowerCase()) || reconciliation.statementPeriod.toLowerCase().includes(searchTerm.toLowerCase());
    // Status filter
    const matchesStatus = filterStatus === 'all' || reconciliation.status.toLowerCase().replace(' ', '-') === filterStatus.toLowerCase();
    return matchesSearch && matchesStatus;
  });
  const getStatusColor = status => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'Needs Review':
        return 'bg-yellow-100 text-yellow-800';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div data-id="element-1562">
      <div className="mb-6 flex justify-between items-center" data-id="element-1563">
        <div data-id="element-1564">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-1565">
            Reconciliation Tasks
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-1566">
            View and manage vendor statement reconciliations
          </p>
        </div>
        <button onClick={onStartNew} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1567">
          <PlusIcon className="h-4 w-4 mr-1" data-id="element-1568" />
          New Reconciliation
        </button>
      </div>
      {/* Search and Filters */}
      <div className="mb-6 bg-white p-4 shadow rounded-lg" data-id="element-1569">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1570">
          <div className="md:col-span-3" data-id="element-1571">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1572">
              Search
            </label>
            <div className="relative rounded-md shadow-sm" data-id="element-1573">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1574">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-1575" />
              </div>
              <input type="text" name="search" id="search" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md p-2 border" placeholder="Search by vendor or statement period" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1576" />
            </div>
          </div>
          <div data-id="element-1577">
            <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1578">
              Status
            </label>
            <select id="status-filter" name="status-filter" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} data-id="element-1579">
              <option value="all" data-id="element-1580">All Statuses</option>
              <option value="completed" data-id="element-1581">Completed</option>
              <option value="in-progress" data-id="element-1582">In Progress</option>
              <option value="needs-review" data-id="element-1583">Needs Review</option>
            </select>
          </div>
        </div>
      </div>
      {/* Reconciliations Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-1584">
        <div className="overflow-x-auto" data-id="element-1585">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1586">
            <thead className="bg-gray-50" data-id="element-1587">
              <tr data-id="element-1588">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1589">
                  Vendor
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1590">
                  Statement Period
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1591">
                  Last Reconciled
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1592">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1593">
                  Matches
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1594">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1595">
              {filteredReconciliations.length > 0 ? filteredReconciliations.map(reconciliation => <tr key={reconciliation.id} className="hover:bg-gray-50" data-id="element-1596">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1597">
                      {reconciliation.vendor}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1598">
                      {reconciliation.statementPeriod}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1599">
                      {reconciliation.lastReconciled || 'Not yet reconciled'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1600">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(reconciliation.status)}`} data-id="element-1601">
                        {reconciliation.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1602">
                      <div className="flex items-center space-x-2" data-id="element-1603">
                        <span className="text-green-600" data-id="element-1604">
                          {reconciliation.matchedCount} matched
                        </span>
                        {reconciliation.unmatchedCount > 0 && <span className="text-red-600" data-id="element-1605">
                            {reconciliation.unmatchedCount} unmatched
                          </span>}
                        {reconciliation.discrepancyCount > 0 && <span className="text-yellow-600" data-id="element-1606">
                            {reconciliation.discrepancyCount} discrepancies
                          </span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1607">
                      <button onClick={() => onViewReconciliation(reconciliation)} className="text-indigo-600 hover:text-indigo-900 mr-3" data-id="element-1608">
                        {reconciliation.status === 'In Progress' ? 'Resume' : 'View'}
                      </button>
                    </td>
                  </tr>) : <tr data-id="element-1609">
                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-1610">
                    No reconciliations found matching your criteria
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}